import { Icon } from '@elements-financial/elements';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateProfileImage } from '@elements-financial/elements';
import NotificationBanner from '../Notification/NotificationBanner';

export interface EditUserAvatarProps {
  familyName: string;
  profilePhoto: string;
  profileColor: string;
}

export const EditUserAvatar: FC<EditUserAvatarProps> = ({ familyName, profilePhoto, profileColor }) => {
  const [profileImageUrl, setProfileImageUrl] = useState(profilePhoto);
  const [showSizeError, setShowSizeError] = useState(false);

  const { loading, fileSelectorRef, onSelectFile: baseOnSelectFile } = useUpdateProfileImage();

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await baseOnSelectFile(e);
  };

  const profileImageAlt = useTranslation('display').t('img-alt-profile-image');

  return (
    <div className="flex items-center">
      <div className="flex flex-col items-start justify-center pb-4">
        <div className="relative h-28 w-28">
          <div
            className="flex h-full w-full items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: profileColor || '#ffffff' }}
          >
            {profileImageUrl ? (
              <img className="h-full w-full object-cover" src={profileImageUrl} alt={profileImageAlt} />
            ) : (
              <span className="font-background-900 text-3xl font-extrabold tracking-[-0.01em] text-darkMode-gray-dark">
                {familyName?.substring(0, 2)}
              </span>
            )}
          </div>
          <button
            onClick={() => !loading && fileSelectorRef.current?.click()}
            className="
              absolute bottom-0 right-0
              h-[40px] w-[40px]
              flex items-center justify-center
              overflow-hidden
              rounded-full border-2
              border-black bg-palette-600
              text-black"
          >
            <Icon icon="pencil" />
          </button>

          <input
            hidden
            type="file"
            id="avatarImage"
            ref={fileSelectorRef}
            accept=".jpg,.jpeg,.png,.bmp,"
            onChange={(e) => {
              const file = e.target.files?.[0];
              const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

              if (file && file.size > maxSizeInBytes) {
                setShowSizeError(true);
                return;
              }

              const url = URL.createObjectURL(file || new File([], ''));
              setProfileImageUrl(url);
              onSelectFile(e);
            }}
          />
        </div>
      </div>

      <NotificationBanner
        className="ml-4 !w-[calc(100vw-128px)] sm:!w-auto"
        show={showSizeError}
        innerKey="file-size-error"
        description="File size exceeds 5 MB. Please choose a smaller file."
        close={() => setShowSizeError(false)}
      />
    </div>
  );
};
